/*
 * Zipline: reprice custom-design cart lines when their quantity changes.
 *
 * A custom ball line sits on a hidden "Custom Print" host variant whose price is the per-dozen
 * price for the quantity tier chosen in the designer. Shopify's cart can change a line's
 * quantity but never its price, so a plain quantity change would keep the old tier's price.
 * For lines carrying _zipline_design_id we ask the app to price the saved design at the new
 * quantity (/apps/designer/api/cart-reprice), add the line on the variant at that price with
 * the same properties, then remove the old line. Other lines use the theme's normal path.
 *
 * Wraps theme.cart.changeItem (assets/theme.js), so the cart page and the cart drawer both go
 * through it. Failures return the {status: 422, message} shape the theme already alerts on.
 */
(function () {
  if (!window.theme || !theme.cart || typeof theme.cart.changeItem !== 'function') return;

  var originalChangeItem = theme.cart.changeItem.bind(theme.cart);
  var FAILED = 'We could not update the price for this design. Please try again, or use Edit design.';

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }
  function failure(message) {
    return Promise.resolve(JSON.stringify({ status: 422, message: message }));
  }
  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }
  function cleanProperties(props) {
    var out = {};
    Object.keys(props || {}).forEach(function (k) {
      if (props[k] !== null && props[k] !== undefined && props[k] !== '') out[k] = props[k];
    });
    return out;
  }

  theme.cart.changeItem = async function (key, qty) {
    if (!qty) return originalChangeItem(key, qty);

    var cart = await fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
    var line = cart && cart.items && cart.items.find(function (i) { return i.key === key; });
    var designId = line && line.properties && line.properties._zipline_design_id;
    if (!designId) return originalChangeItem(key, qty);

    var priced = await post('/apps/designer/api/cart-reprice', { designId: designId, dozens: qty })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
    if (!priced || !priced.variantId) return failure(FAILED);

    // Same price tier: the current variant is already right, only the quantity changes.
    if (String(priced.variantId) === String(line.variant_id)) return originalChangeItem(key, qty);

    var props = cleanProperties(line.properties);
    if (priced.detail && priced.detail.length) props['Print details'] = priced.detail.join('; ');

    // A newly created price variant takes ~6 to 17 s to become sellable (same retry as the
    // designer's Add to Cart: 12 attempts, 1 s growing to 3 s, ~28 s in all). Reachable prices are
    // normally pre-created by the app, so this usually succeeds first time. The old line stays
    // until the new one is in, so a failure never leaves the customer with a missing line.
    var lastMessage = '';
    for (var attempt = 0; attempt < 12; attempt++) {
      var add = await post('/cart/add.js', {
        items: [{ id: Number(priced.variantId), quantity: qty, properties: props }]
      });
      if (add.ok) return originalChangeItem(key, 0);
      var body = await add.json().catch(function () { return {}; });
      lastMessage = (body && (body.description || body.message)) || '';
      if (add.status === 422 && /sold out|not available/i.test(lastMessage)) {
        if (attempt < 11) await sleep(Math.min(1000 + 500 * attempt, 3000));
        continue;
      }
      break;
    }
    return failure(lastMessage ? FAILED + ' (' + lastMessage + ')' : FAILED);
  };
})();
